import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { Icon } from '@kiips/ui';
import { Pressable, Linking, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Text, YStack, XStack } from 'tamagui';
import Constants from 'expo-constants';
import { useAuth } from './AuthContext';
import { trpc } from '../lib/trpc';
import { useThemeColors } from '../hooks/useThemeColors';
import { colors as staticColors } from '../theme/colors';
import { useTheme } from './ThemeContext';
import { useUnifiedSidebar } from './UnifiedSidebarContext';
import { useSidebarSetters } from './SidebarContentContext';
import { FallbackImage } from '../components/design/FallbackImage';

type MenuContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const MenuContext = createContext<MenuContextValue | undefined>(undefined);

export function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within MenuProvider');
  return ctx;
}

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const { activeSidebar, open: openUnified, close: closeUnified } = useUnifiedSidebar();
  const { setMenu } = useSidebarSetters();
  const isOpen = activeSidebar === 'menu';
  const router = useRouter();
  const { isAuthenticated, user, logout } = useAuth();
  const colors = useThemeColors();
  const { mode, setMode } = useTheme();
  const billingPortal = trpc.billing.createBillingPortalSession.useMutation({
    onSuccess: (data) => {
      if (data.url) void Linking.openURL(data.url);
    },
  });
  const open = useCallback(() => openUnified('menu'), [openUnified]);
  const close = useCallback(() => closeUnified(), [closeUnified]);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  const handleNav = useCallback(
    (path: string) => {
      close();
      router.push(path);
    },
    [close, router]
  );

  const handleSignOut = useCallback(async () => {
    close();
    await logout();
    router.replace('/');
  }, [close, logout, router]);

  const value: MenuContextValue = { isOpen, open, close, toggle };

  const avatarLabel = isAuthenticated && user
    ? (user.displayName ?? user.email)
    : 'Guest';

  const primaryNavItems = useMemo(
    () => [
      { icon: 'home' as const, label: 'Home', path: '/' },
      { icon: 'flight' as const, label: 'My Trips', path: '/trips' },
    ],
    [],
  );

  const rowStyle = useCallback(
    (pressed: boolean) => ({
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 12,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 14,
      backgroundColor: pressed ? colors.borderMuted : 'transparent',
    }),
    [colors.borderMuted],
  );

  const menuContent = useMemo(() => (
    <YStack flex={1} gap={0}>
      {/* Profile */}
      <YStack paddingBottom={20}>
        <YStack width={64} height={64} borderRadius={32} overflow="hidden" marginBottom={14}>
          <FallbackImage
            uri={isAuthenticated && user ? user.avatarUrl : null}
            label={avatarLabel}
            style={{ width: 64, height: 64 }}
            borderRadius={32}
            letterSize={26}
          />
        </YStack>
        {isAuthenticated && user ? (
          <Pressable onPress={() => handleNav('/profile')}>
            <Text fontFamily="$body" fontSize={20} fontWeight="800" color="$textOnSurface" numberOfLines={1}>
              {user.displayName ?? user.email.split('@')[0] ?? 'Traveller'}
            </Text>
            {user.email ? (
              <Text fontFamily="$body" fontSize={13} color="$textSubdued" marginTop={2} numberOfLines={1}>
                {user.email}
              </Text>
            ) : null}
            <XStack alignItems="center" gap={4} marginTop={6}>
              <Text fontFamily="$body" fontSize={11} color="$textSubdued" fontWeight="700" letterSpacing={0.4}>
                {user.subscriptionTier.toUpperCase()}
              </Text>
              <Icon name="chevron-right" size={13} color={colors.textSubdued} />
            </XStack>
          </Pressable>
        ) : (
          <YStack gap={4}>
            <Text fontFamily="$body" fontSize={20} fontWeight="800" color="$textOnSurface">
              Hello, traveller
            </Text>
            <Text fontFamily="$body" fontSize={13} color="$textSubdued">
              Sign in to save your trips
            </Text>
          </YStack>
        )}
      </YStack>

      {/* Appearance */}
      <YStack paddingBottom={20}>
        <Text fontFamily="$body" fontSize={11} color="$textSubdued" fontWeight="700" letterSpacing={0.6} marginBottom={8}>
          APPEARANCE
        </Text>
        <XStack gap={6}>
          {(['light', 'dark', 'system'] as const).map((m) => (
            <Pressable
              key={m}
              onPress={() => setMode(m)}
              style={{
                flex: 1,
                paddingVertical: 9,
                borderRadius: 12,
                backgroundColor: mode === m ? colors.textOnSurface : colors.borderMuted,
                alignItems: 'center',
              }}
            >
              <Text
                fontFamily="$body"
                fontSize={12}
                fontWeight="600"
                style={{ color: mode === m ? colors.textInverse : colors.textSubdued }}
              >
                {m === 'system' ? 'Auto' : m === 'light' ? 'Light' : 'Dark'}
              </Text>
            </Pressable>
          ))}
        </XStack>
      </YStack>

      <View style={{ height: 1, backgroundColor: colors.dividerSubtle, marginBottom: 16 }} />

      {/* Nav */}
      <YStack gap={2} flex={1}>
        {primaryNavItems.map(({ icon, label, path }) => (
          <Pressable
            key={path}
            onPress={() => handleNav(path)}
            style={({ pressed }) => rowStyle(pressed)}
          >
            <YStack
              width={36}
              height={36}
              borderRadius={10}
              backgroundColor="$borderMuted"
              alignItems="center"
              justifyContent="center"
            >
              <Icon name={icon} size={18} color={colors.textOnSurface} />
            </YStack>
            <Text fontFamily="$body" fontSize={15} fontWeight="600" color="$textOnSurface" flex={1}>
              {label}
            </Text>
            <Icon name="chevron-right" size={18} color={colors.textSubdued} />
          </Pressable>
        ))}

        <Pressable
          onPress={() => handleNav('/contact-us')}
          style={({ pressed }) => rowStyle(pressed)}
        >
          <YStack
            width={36}
            height={36}
            borderRadius={10}
            backgroundColor="$borderMuted"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="feedback" size={18} color={colors.textOnSurface} />
          </YStack>
          <Text fontFamily="$body" fontSize={15} fontWeight="600" color="$textOnSurface" flex={1}>
            Contact us
          </Text>
          <Icon name="chevron-right" size={18} color={colors.textSubdued} />
        </Pressable>

        {isAuthenticated && user ? (
          <Pressable
            onPress={() => {
              close();
              const returnUrl =
                (Constants.expoConfig?.extra as { billingReturnUrl?: string | undefined} | undefined)
                  ?.billingReturnUrl ?? 'http://localhost:19000';
              billingPortal.mutate({ returnUrl });
            }}
            disabled={billingPortal.isPending}
            style={({ pressed }) => rowStyle(pressed)}
          >
            <YStack
              width={36}
              height={36}
              borderRadius={10}
              backgroundColor="$borderMuted"
              alignItems="center"
              justifyContent="center"
            >
              <Icon name="credit-card" size={18} color={colors.textOnSurface} />
            </YStack>
            <Text fontFamily="$body" fontSize={15} fontWeight="600" color="$textOnSurface" flex={1}>
              {billingPortal.isPending ? 'Opening…' : 'Subscription'}
            </Text>
            <Icon name="chevron-right" size={18} color={colors.textSubdued} />
          </Pressable>
        ) : null}
      </YStack>

      {/* Auth footer */}
      {isAuthenticated ? (
        <Pressable
          onPress={handleSignOut}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 16, paddingBottom: 4, paddingHorizontal: 12 }}
        >
          <Icon name="logout" size={16} color={colors.destructive} />
          <Text fontFamily="$body" fontSize={13} fontWeight="600" style={{ color: colors.destructive }}>
            Sign out
          </Text>
        </Pressable>
      ) : (
        <YStack gap={8} paddingTop={16}>
          <Pressable
            onPress={() => handleNav('/login')}
            style={({ pressed }) => ({
              backgroundColor: colors.textOnSurface,
              borderRadius: 24,
              paddingVertical: 14,
              alignItems: 'center',
              opacity: pressed ? 0.85 : 1,
            })}
          >
            <Text fontFamily="$body" fontSize={15} fontWeight="700" style={{ color: colors.textInverse }}>
              Sign in
            </Text>
          </Pressable>
          <Pressable
            onPress={() => handleNav('/signup')}
            style={{ paddingVertical: 10, alignItems: 'center' }}
          >
            <Text fontFamily="$body" fontSize={13} fontWeight="600" color="$textSubdued">
              Create account
            </Text>
          </Pressable>
        </YStack>
      )}

      <YStack alignItems="center" paddingTop={12}>
        <Text fontFamily="$body" fontSize={10} color="$textSubdued" style={{ letterSpacing: 0.4 }}>
          {(() => {
            const extra = (Constants.expoConfig?.extra ?? {}) as { buildSha?: string | undefined; buildTime?: string | undefined};
            return `build ${extra.buildSha ?? 'dev'}${extra.buildTime ? ` · ${extra.buildTime}` : ''}`;
          })()}
        </Text>
      </YStack>
    </YStack>
  ), [
    isAuthenticated,
    user,
    avatarLabel,
    handleNav,
    handleSignOut,
    close,
    mode,
    setMode,
    colors,
    billingPortal,
    primaryNavItems,
    rowStyle,
  ]);

  useEffect(() => {
    if (isOpen) {
      setMenu(menuContent, close);
    } else {
      setMenu(null, undefined);
    }
    return () => setMenu(null, undefined);
  }, [isOpen, setMenu, close, menuContent]);

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

/** Hamburger button for use in headerRight */
export function HamburgerButton() {
  const { toggle } = useMenu();
  return (
    <Pressable onPress={toggle} accessibilityRole="button" style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', marginRight: 4 }} accessibilityLabel="Menu">
      <Icon name="menu" size={24} color={staticColors.textOnDark} />
    </Pressable>
  );
}
